import RadioGroup from '@mui/material/RadioGroup';
import { formatDistanceToNow, subDays } from 'date-fns';
import * as _ from 'lodash';
import { useState } from 'react';
import {
  ButtonGroup,
  Checkbox,
  ControlLabel,
  DropdownButton,
  FormGroup,
  ListGroup,
  ListGroupItem,
  MenuItem,
} from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { REQUIRE_TEAM_ON_UPLOAD } from '../../../../../shared/featureFlags/flagNames';
import { plural } from '../../../../../shared/helpers/plural';
import { MAX_IMPORT_REPOS } from '../../../../../shared/types/routes/vcs/githubApp';
import { window } from '../../../../globals';
import { isFeatureEnabled } from '../../../../helpers/features/featureFlags';
import { shouldShowQuickImportScanMethods } from '../../../../helpers/features/quickImportScanMethods';
import { userHasPermissionToViewAnyProject } from '../../../../helpers/permissions';
import { useAllGitHubAppRepositories } from '../../../../hooks/api/vcs/githubApp/useAllGitHubAppRepositories';
import LabeledRadio from '../../../generic/Forms/LabeledRadio';
import Icon, { icons, IconText } from '../../../generic/Icon';
import Link from '../../../generic/Link/Link';
import Loader from '../../../generic/Loader';
import Text from '../../../generic/Text';
import ModalWizard from '../../../modals/ModalWizard';
import SelectTeam, { teamsUserCanAddProject } from '../../SelectTeam';
import QuickImportScanMethodsFields from '../QuickImportScanMethodsFields';
import type { Team } from '../../../../../shared/model-typings/team';
import type { VcsRepository } from '../../../../../shared/types/routes/vcs/githubApp';
import type { VcsApp } from '../../../../../shared/vcs/githubApp/apps';
import type { ScanMethodSelections } from '../types';

import { isRepoEligibleForImportAll } from './importAllFilters';
import type { ImportAllFilters } from './importAllFilters';

// Mirrors legacy ModalBulkImport's step constants: positions in the unfiltered step list.
const IMPORT_STEP_REVIEW_PROJECTS = 1;
const IMPORT_STEP_CONFIGURE_IMPORT = 2;
const IMPORT_STEP_SELECT_TEAMS = 3;
const IMPORT_STEP_SCAN_METHODS = 4;
const WIZARD_STEP_POSITIONS = [
  IMPORT_STEP_REVIEW_PROJECTS,
  IMPORT_STEP_CONFIGURE_IMPORT,
  IMPORT_STEP_SELECT_TEAMS,
  IMPORT_STEP_SCAN_METHODS,
];

const REPO_LIST_LENGTH = 7;

/** Everything the import-all wizard collects, handed to the container on Confirm. */
export type VcsImportAllOptions = {
  policyNotifications: boolean;
  policyUpdate: 'always' | 'organization';
  policyAccess: 'default' | 'public' | 'private';
  submitBadgePRs: boolean;
  teamIds: number[];
  quickImportVendoredDetectionEnabled: boolean;
  quickImportSnippetDetectionEnabled: boolean;
  quickImportShouldSavePreference: boolean;
};

type VcsImportAllModalProps = {
  show: boolean;
  /** The vcs app the page is scoped to; must be the app `connectionId` was listed under. */
  app: VcsApp;
  /** Display name of the account whose repos will be imported (title and loader copy). */
  accountName: string;
  connectionId: string;
  allTeams: Team[];
  onHide: () => void;
  onFinish: (repos: VcsRepository[], options: VcsImportAllOptions) => void;
};

/**
 * The vcs-service "Import All" wizard, a functional clone of the legacy `ModalBulkImport`:
 * same steps (Review Projects, Configure Import, Select Teams, Scan Methods), same filters,
 * and the same review UX (gathering loader, live count, 7-repo preview). Like legacy, it
 * enumerates the account client-side and hands the filtered repo list to the container on
 * Confirm; the existing import endpoint queues large batches in the background.
 */
export const VcsImportAllModal: VoidFunctionComponent<VcsImportAllModalProps> = ({
  show,
  app,
  accountName,
  connectionId,
  allTeams,
  onHide,
  onFinish,
}) => {
  const [filters, setFilters] = useState<ImportAllFilters>({
    access: 'all',
    excludeForks: false,
    updatedWithinDays: null,
  });
  const [policyNotifications, setPolicyNotifications] = useState<'true' | 'false'>('true');
  const [policyUpdate, setPolicyUpdate] = useState<VcsImportAllOptions['policyUpdate']>('always');
  const [policyAccess, setPolicyAccess] = useState<VcsImportAllOptions['policyAccess']>('default');
  // Defaults off, matching the legacy modal (the selection flow's sidebar checkbox defaults on).
  const [shouldSendBadgePR, setShouldSendBadgePR] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [scanSelections, setScanSelections] = useState<ScanMethodSelections>({
    quickImportVendoredDetectionEnabled: false,
    quickImportSnippetDetectionEnabled: false,
    quickImportShouldSavePreference: false,
  });

  const organizationId = window.user?.organizationId;

  const isEnumerationEnabled = show && Boolean(connectionId);
  const { data: allRepos, isError, isFetching, error } = useAllGitHubAppRepositories(app, connectionId, {
    enabled: isEnumerationEnabled,
  });
  // Like legacy, the review step shows the gathering loader until the enumeration lands —
  // including reopens, where react-query refetches but still holds the previous result. A
  // disabled enumeration (no connection to list) must not read as gathering, or the loader
  // would spin forever.
  const isGathering = isEnumerationEnabled && (isFetching || (!isError && allRepos === undefined));

  const now = new Date();
  const eligibleRepos = (allRepos ?? []).filter((repo) => isRepoEligibleForImportAll(repo, filters, now));
  // The import endpoint accepts at most MAX_IMPORT_REPOS per request; a note in the review
  // step surfaces the truncation (the legacy modal's 1000-repo note, at the higher bound).
  const repos = eligibleRepos.slice(0, MAX_IMPORT_REPOS);
  // Badge PRs only apply to public repos, so an all-private import disables the option.
  const hasPublicRepos = repos.some((repo) => !repo.isPrivate);

  const showWizardStep = (step: number) => {
    if (step === IMPORT_STEP_SCAN_METHODS) {
      // The scan-methods fields fetch org-level settings, so the step needs an organization.
      return shouldShowQuickImportScanMethods() && Boolean(organizationId);
    }
    if (step === IMPORT_STEP_SELECT_TEAMS) {
      return teamsUserCanAddProject(allTeams).length > 0;
    }
    return true;
  };

  // ModalWizard numbers `canNextStep` steps after `showStep` filtering, while the step
  // constants are unfiltered positions; map back so hiding a step doesn't shift the gates
  // onto the step that takes its place.
  const toUnfilteredStep = (filteredStep: number) =>
    WIZARD_STEP_POSITIONS.filter(showWizardStep)[filteredStep - 1];

  const canMoveToNextWizardStep = (currentStep: number) => {
    if (!connectionId || isGathering || isError) {
      return false;
    }

    const unfilteredStep = toUnfilteredStep(currentStep);
    // A string disables Next and surfaces as a tooltip explaining why.
    if (unfilteredStep === IMPORT_STEP_REVIEW_PROJECTS && repos.length === 0) {
      return 'No repositories match the selected filters.';
    }
    if (unfilteredStep === IMPORT_STEP_SELECT_TEAMS) {
      return userHasPermissionToViewAnyProject() || selectedTeams.length > 0;
    }
    return true;
  };

  const renderImport = () => (
    <div>
      <div className="solution-banner">
        <h4 style={{ marginBottom: '5px' }}>
          <IconText
            icon={icons.warning}
            text={`FOSSA will import ${repos.length} ${plural(repos.length, 'repository', 'repositories')}`}
          />
        </h4>
        {eligibleRepos.length > MAX_IMPORT_REPOS && (
          <Text.Micro>
            Note: Due to import limits, FOSSA will only import the first {MAX_IMPORT_REPOS} repositories.
          </Text.Micro>
        )}
        <b className="inline-header">Filter:</b>
        <br />
        <ButtonGroup>
          <DropdownButton
            id="import-repository-privacy-filter"
            title={
              <span>
                {filters.access === 'all' ? 'Public & private' : `Only ${filters.access}`}
              </span>
            }
            onSelect={(access: unknown) =>
              setFilters((current) => ({ ...current, access: access as ImportAllFilters['access'] }))}>
            <MenuItem eventKey="all" active={filters.access === 'all'}>
              All
            </MenuItem>
            <MenuItem eventKey="public" active={filters.access === 'public'}>
              Public
            </MenuItem>
            <MenuItem eventKey="private" active={filters.access === 'private'}>
              Private
            </MenuItem>
          </DropdownButton>
          <DropdownButton
            id="import-repository-last-updated-filter"
            title={
              <span>
                Updated{' '}
                {filters.updatedWithinDays
                  ? formatDistanceToNow(subDays(new Date(), filters.updatedWithinDays), { addSuffix: true })
                  : 'anytime'}
              </span>
            }
            onSelect={(updatedWithinDays: unknown) =>
              setFilters((current) => ({
                ...current,
                updatedWithinDays: updatedWithinDays as ImportAllFilters['updatedWithinDays'],
              }))}>
            <MenuItem eventKey={null} active={filters.updatedWithinDays === null}>
              Anytime
            </MenuItem>
            <MenuItem eventKey={30} active={filters.updatedWithinDays === 30}>
              Last 30 days
            </MenuItem>
            <MenuItem eventKey={90} active={filters.updatedWithinDays === 90}>
              Last 90 days
            </MenuItem>
            <MenuItem eventKey={365} active={filters.updatedWithinDays === 365}>
              Last year
            </MenuItem>
          </DropdownButton>
          <DropdownButton
            id="import-repository-include-forks-filter"
            title={<span>Forks {!filters.excludeForks ? 'included' : 'excluded'}</span>}
            onSelect={(excludeForks: unknown) =>
              setFilters((current) => ({ ...current, excludeForks: Boolean(excludeForks) }))}>
            <MenuItem eventKey={false} active={!filters.excludeForks}>
              Included
            </MenuItem>
            <MenuItem eventKey active={filters.excludeForks}>
              Excluded
            </MenuItem>
          </DropdownButton>
        </ButtonGroup>
      </div>
      <br />
      {repos.length > 0 ? (
        <ListGroup>
          {repos.slice(0, REPO_LIST_LENGTH).map((repo) => (
            <ListGroupItem key={repo.locator || repo.id}>
              <Link to={repo.url} openInNewTab>
                {repo.title}{' '}<Icon icon={icons.externalLink} />
              </Link>
            </ListGroupItem>
          ))}
          {repos.length > REPO_LIST_LENGTH ? (
            <ListGroupItem className="text-center">
              <i>and {repos.length - REPO_LIST_LENGTH} more...</i>
            </ListGroupItem>
          ) : null}
        </ListGroup>
      ) : (
        <div className="alert">No repos to preview</div>
      )}
    </div>
  );

  return (
    <ModalWizard
      show={show}
      onHide={onHide}
      showStep={showWizardStep}
      canNextStep={canMoveToNextWizardStep}
      title={`Import all repositories from "${accountName}"`}
      stepNames={['Review Projects', 'Configure Import', 'Select Teams', 'Scan Methods']}
      finishText={
        <span>
          Confirm Import <b>{repos.length}</b> {plural(repos.length, 'Repo')}
        </span>
      }
      onFinish={() => {
        // The review step blocks Next when nothing matches the filters, but ModalWizard's
        // arrow-key navigation skips canNextStep, so keep the no-op guard here too.
        if (repos.length === 0) {
          return false;
        }
        onFinish(repos, {
          policyNotifications: policyNotifications === 'true',
          policyUpdate,
          policyAccess,
          // Derived at submit, not just in the UI: the box may have been checked before a
          // filter change removed the last public repo.
          submitBadgePRs: hasPublicRepos && shouldSendBadgePR,
          teamIds: selectedTeams.map((team) => team.id),
          quickImportVendoredDetectionEnabled: scanSelections.quickImportVendoredDetectionEnabled,
          quickImportSnippetDetectionEnabled: scanSelections.quickImportSnippetDetectionEnabled,
          quickImportShouldSavePreference: scanSelections.quickImportShouldSavePreference,
        });
        return undefined;
      }}>
      <div>
        {!connectionId && (
          <div className="card card-error">No account is available to import from.</div>
        )}
        {isGathering && (
          <div className="card card-primary text-center">
            <Loader color="#d2b690" />
            <h4>
              Gathering all repositories in <b>{accountName}</b> for review...
            </h4>
            <p>This could take a couple minutes.</p>
          </div>
        )}
        {Boolean(connectionId) && !isGathering && isError && (
          <div className="card card-error">{_.isError(error) ? error.message : 'Failed to gather repositories.'}</div>
        )}
        {Boolean(connectionId) && !isGathering && !isError && renderImport()}
      </div>
      <div>
        <FormGroup className="card">
          <h4>Notifications</h4>
          <RadioGroup
            name="policy_notifications"
            defaultValue={policyNotifications}
            onChange={(event) => setPolicyNotifications(event.target.value as 'true' | 'false')}
          >
            <LabeledRadio value="true" label="Enable notifications (recommended for &lt;20 repos)" />
            <LabeledRadio value="false" label="Configure notifications later" />
          </RadioGroup>
        </FormGroup>
        <FormGroup className="card">
          <h4>Updates</h4>
          <RadioGroup
            name="policy_update"
            defaultValue={policyUpdate}
            onChange={(event) => setPolicyUpdate(event.target.value as VcsImportAllOptions['policyUpdate'])}>
            <LabeledRadio value="always" label="Per-commit" />
            <LabeledRadio value="organization" label="Use organization defaults" />
          </RadioGroup>
        </FormGroup>
        <FormGroup className="card">
          <h4>Access</h4>
          <RadioGroup
            name="policy_access"
            defaultValue={policyAccess}
            onChange={(event) => setPolicyAccess(event.target.value as VcsImportAllOptions['policyAccess'])}
          >
            <LabeledRadio value="default" label="Default (as-is based off repo permissions)" />
            <LabeledRadio value="public" label="Set all public" />
            <LabeledRadio value="private" label="Set all private" />
          </RadioGroup>
        </FormGroup>
        <FormGroup className="card">
          <ControlLabel>Misc</ControlLabel>
          <Checkbox
            checked={hasPublicRepos && shouldSendBadgePR}
            disabled={!hasPublicRepos}
            onChange={() => setShouldSendBadgePR((current) => !current)}
          >
            Submit badge PRs after import (public GitHub READMEs only)
          </Checkbox>
          {!hasPublicRepos && (
            <Text.Micro>No public repositories in this import.</Text.Micro>
          )}
        </FormGroup>
      </div>
      <div>
        <h4>Assign Project to Teams</h4>
        <SelectTeam
          allTeams={allTeams}
          selectedTeams={selectedTeams}
          onTeamsSelected={setSelectedTeams}
          isRequired={isFeatureEnabled(REQUIRE_TEAM_ON_UPLOAD)}
        />
      </div>
      <div>
        {organizationId && (
          <QuickImportScanMethodsFields
            organizationId={organizationId}
            enabled={show}
            onChange={setScanSelections}
          />
        )}
      </div>
    </ModalWizard>
  );
};
